<script lang="ts">
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Hero from '$lib/components/sections/Hero.svelte';
	import StorySection from '$lib/components/sections/StorySection.svelte';
	import AllFeatures from '$lib/components/sections/AllFeatures.svelte';
	import CustomerQuote from '$lib/components/sections/CustomerQuote.svelte';
	import SocialProof from '$lib/components/sections/SocialProof.svelte';
	import WhoIsThisFor from '$lib/components/sections/WhoIsThisFor.svelte';
	import FinalCTA from '$lib/components/sections/FinalCTA.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import { initSmoothScroll } from '$lib/utils/smoothScroll';
	import { observeElements } from '$lib/utils/animations';
	import type { PageData } from './$types';

	let { data }: { data: PageData } = $props();

	// Use server-provided SEO metadata for faster rendering
	// Keyword-first, brand-last title (≤60 chars) for the primary keyword.
	const seoTitle = `${data.meta.title} | Heyweek`;
	const seoDescription = data.meta.description;
	const seoKeywords = data.meta.keywords;
	const siteUrl = 'https://heyweek.com';

	onMount(() => {
		initSmoothScroll();
		observeElements();

		// Initialize performance monitoring
		import('$lib/utils/performance').then(
			({ initWebVitals, prefetchResources, optimizeCriticalPath }) => {
				optimizeCriticalPath();
				initWebVitals();
				prefetchResources();
			}
		);

		// Initialize progressive enhancements
		import('$lib/utils/progressive').then(({ initProgressiveEnhancements }) => {
			initProgressiveEnhancements();
		});
	});
</script>

<svelte:head>
	<title>{seoTitle}</title>
	<meta name="description" content={seoDescription} />
	<meta name="keywords" content={seoKeywords} />

	<!-- Open Graph / Facebook -->
	<meta property="og:type" content="website" />
	<meta property="og:url" content={siteUrl} />
	<meta property="og:title" content={seoTitle} />
	<meta property="og:description" content={seoDescription} />

	<!-- Twitter (image + card supplied globally in app.html) -->
	<meta name="twitter:url" content={siteUrl} />
	<meta name="twitter:title" content={seoTitle} />
	<meta name="twitter:description" content={seoDescription} />

	<!-- Additional SEO tags -->
	<link rel="canonical" href={siteUrl} />
	<meta name="robots" content="index, follow" />
	<meta name="author" content="Heyweek" />
	<meta name="viewport" content="width=device-width, initial-scale=1" />

	<!-- Schema.org structured data.
	     NOTE: a SoftwareApplication block was removed here. Google's Software App
	     rich result requires `aggregateRating` (or `review`), which we can't add
	     without real review data — faking ratings violates Google's guidelines.
	     Without it the block only ever produced a "rich results validation error"
	     (flagged by Ahrefs) with no upside. Organization + WebSite below give the
	     same brand/entity understanding for search and AI engines, and are valid.
	     Re-add SoftwareApplication WITH aggregateRating once real reviews exist. -->

	<!-- Organization entity is emitted site-wide from +layout.svelte, so it's not
	     repeated here (avoids a duplicate Organization block on the homepage). -->

	<!-- WebSite entity with sitelinks search box -->
	{@html `<script type="application/ld+json">
	{
		"@context": "https://schema.org",
		"@type": "WebSite",
		"name": "Heyweek",
		"url": "${siteUrl}",
		"potentialAction": {
			"@type": "SearchAction",
			"target": {
				"@type": "EntryPoint",
				"urlTemplate": "${siteUrl}/search?q={search_term_string}"
			},
			"query-input": "required name=search_term_string"
		}
	}
	</script>`}
</svelte:head>

<Header />

<main>
	<Hero />
	<StorySection />
	<AllFeatures />
	<CustomerQuote />
	<SocialProof />
	<WhoIsThisFor />
	<FinalCTA />
</main>

<Footer />
